import { create } from "zustand";

export type PaneViewType = "editor" | "graph" | "settings" | "welcome";
export type ViewModeType = "web" | "normal" | "dashboard";

export interface PaneState {
  viewType: PaneViewType;
  activeFileId: string | null;
  openTabs: string[];
  editorMode: "edit" | "preview" | "flow";
}

interface PanesState {
  activePane: "left" | "right";
  isSplit: boolean;
  leftPane: PaneState;
  rightPane: PaneState;
  currentViewMode: ViewModeType;

  setActivePane: (pane: "left" | "right") => void;
  setIsSplit: (split: boolean) => void;
  openFile: (fileId: string, pane: "left" | "right") => void;
  closeFile: (fileId: string, pane: "left" | "right") => void;
  setActiveTab: (fileId: string, pane: "left" | "right") => void;
  setEditorMode: (mode: "edit" | "preview" | "flow", pane: "left" | "right") => void;
  setPaneViewType: (viewType: PaneViewType, pane: "left" | "right") => void;
  setCurrentViewMode: (mode: ViewModeType) => void;
  reorderTabs: (fromIndex: number, toIndex: number, pane: "left" | "right") => void;
  swapFileId: (oldId: string, newId: string) => void;
}

const DEFAULT_PANE: PaneState = {
  viewType: "welcome",
  activeFileId: null,
  openTabs: [],
  editorMode: "edit",
};

export const usePanesStore = create<PanesState>((set, get) => ({
  activePane: "left",
  isSplit: false,
  leftPane: { ...DEFAULT_PANE },
  rightPane: { ...DEFAULT_PANE },
  currentViewMode: "dashboard",

  setActivePane: (pane) => set({ activePane: pane }),
  setIsSplit: (split) => set({ isSplit: split }),

  openFile: (fileId, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      const openTabs = current.openTabs.includes(fileId) ? current.openTabs : [...current.openTabs, fileId];
      const updated: PaneState = { ...current, viewType: "editor", activeFileId: fileId, openTabs };
      return pane === "left"
        ? { leftPane: updated, activePane: "left", currentViewMode: "normal" }
        : { rightPane: updated, activePane: "right", currentViewMode: "normal" };
    });
  },

  closeFile: (fileId, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      const openTabs = current.openTabs.filter((id) => id !== fileId);
      let activeFileId = current.activeFileId;
      let viewType = current.viewType;
      if (activeFileId === fileId) {
        activeFileId = openTabs.length > 0 ? openTabs[openTabs.length - 1] : null;
        viewType = activeFileId ? "editor" : "welcome";
      }
      const updated: PaneState = { ...current, openTabs, activeFileId, viewType };
      return pane === "left" ? { leftPane: updated } : { rightPane: updated };
    });
  },

  setActiveTab: (fileId, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      const updated: PaneState = { ...current, viewType: "editor", activeFileId: fileId };
      return pane === "left"
        ? { leftPane: updated, activePane: "left", currentViewMode: "normal" }
        : { rightPane: updated, activePane: "right", currentViewMode: "normal" };
    });
  },

  setEditorMode: (mode, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      const updated: PaneState = { ...current, editorMode: mode };
      return pane === "left" ? { leftPane: updated } : { rightPane: updated };
    });
  },

  setPaneViewType: (viewType, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      const updated: PaneState = { ...current, viewType };
      return pane === "left"
        ? { leftPane: updated, activePane: "left", currentViewMode: "normal" }
        : { rightPane: updated, activePane: "right", currentViewMode: "normal" };
    });
  },

  setCurrentViewMode: (mode) => set({ currentViewMode: mode }),

  reorderTabs: (fromIndex, toIndex, pane) => {
    set((s) => {
      const current = pane === "left" ? s.leftPane : s.rightPane;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= current.openTabs.length || toIndex >= current.openTabs.length) {
        return {};
      }
      const openTabs = [...current.openTabs];
      const [moved] = openTabs.splice(fromIndex, 1);
      openTabs.splice(toIndex, 0, moved);
      const updated: PaneState = { ...current, openTabs };
      return pane === "left" ? { leftPane: updated } : { rightPane: updated };
    });
  },

  swapFileId: (oldId, newId) => {
    set((s) => {
      const updatePane = (pane: PaneState): PaneState => {
        const activeFileId = pane.activeFileId === oldId ? newId : pane.activeFileId;
        const openTabs = pane.openTabs.map((id) => (id === oldId ? newId : id));
        return { ...pane, activeFileId, openTabs };
      };
      return {
        leftPane: updatePane(s.leftPane),
        rightPane: updatePane(s.rightPane),
      };
    });
  },
}));